/**
 * Persist where the user started a SlickPay Open Banking checkout so the
 * callback page can return them there after Hope Macy redirects back.
 */

export type SlickPayResultKind = 'success' | 'failed' | 'pending';

export type SlickPayResultState = {
  slickpayResult: SlickPayResultKind;
  message?: string;
};

const ORIGIN_KEY_PREFIX = 'slickpay_return_origin_';

export function slickPayInvoiceOriginKey(invoiceId: string): string {
  return `${ORIGIN_KEY_PREFIX}invoice_${invoiceId}`;
}

export function slickPayCreditsOriginKey(unitId: string): string {
  return `${ORIGIN_KEY_PREFIX}credits_${unitId}`;
}

/**
 * Store the post-payment redirect target under `key`. Defaults to the current
 * pathname + search, but callers can pass an explicit `path` to send the user
 * somewhere more useful than the page checkout was started from (e.g. the
 * RSVP dashboard for the event the invoice belongs to).
 */
export function saveSlickPayReturnOrigin(key: string, path?: string): void {
  if (typeof window === 'undefined') return;
  try {
    const origin = path ?? `${window.location.pathname}${window.location.search}`;
    localStorage.setItem(key, origin);
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Read and remove the stored origin. Returns `fallback` when missing or invalid
 * (e.g. absolute URL, empty string).
 */
export function consumeSlickPayReturnOrigin(key: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  try {
    const stored = localStorage.getItem(key);
    localStorage.removeItem(key);
    if (!stored || typeof stored !== 'string') return fallback;
    const trimmed = stored.trim();
    if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return fallback;
    return trimmed;
  } catch {
    return fallback;
  }
}

const PENDING_PO_ID_KEY = 'slickpay_pending_po_id';

/**
 * SlickPay has one fixed return URL shared by every checkout flow, so the
 * return page cannot rely on a route param to know which order it's for.
 * Hope Macy normally appends the poId as a query param, but as a backstop
 * for the same browser session, remember the most recently started order.
 */
export function savePendingSlickPayOrder(poId: string): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PENDING_PO_ID_KEY, poId);
  } catch {
    /* ignore quota / private mode */
  }
}

/** Read and remove the most recently saved pending poId, if any. */
export function consumePendingSlickPayOrder(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(PENDING_PO_ID_KEY);
    localStorage.removeItem(PENDING_PO_ID_KEY);
    return stored && typeof stored === 'string' ? stored : null;
  } catch {
    return null;
  }
}
